import * as Animate from "react-reveal";
import { useEffect, useRef, useState } from "react";
import styles from "./homeCarousel.module.css";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "../../../components/svgs";
import { getCohortsApi, getCoursesApi } from "../../../store/actions";

const accents = {
  blue: "#12136d",
  yellow: "#8c5c00",
  violet: "#5a35b2",
  green: "#087250",
  orange: "#9d3e0d",
  rose: "#9b2450",
  teal: "#0b5f6e",
};

const courses = [
  {
    accent: "blue",
    mark: "DA",
    shortcut: "DATA",
    tagline: "Become a Data Analyst",
    title: "Data Analytics Bootcamp",
    apiTitle: "Data Analytics Bootcamp",
    description:
      "Learn to clean, analyse and communicate data by working with realistic business datasets.",
    duration: "3 months",
    format: "Live online",
    schedule: "Weekends",
  },
  {
    accent: "teal",
    mark: "DS",
    shortcut: "SCIENCE",
    tagline: "Become a Data Scientist",
    title: "Data Science Bootcamp",
    apiTitle: "Data Science Bootcamp",
    description:
      "Analyse, visualise and predict with data using Python, SQL and machine learning on real-world projects.",
    duration: "6 months",
    format: "Live online",
    schedule: "Weekends",
  },
  {
    accent: "yellow",
    mark: "AI",
    shortcut: "AI",
    tagline: "Build AI-powered workflows",
    title: "AI & Automation Bootcamp",
    apiTitle: "AI Automation Diploma",
    description:
      "Build practical AI workflows, automations and intelligent systems for modern business problems.",
    duration: "12 weeks",
    format: "Live online",
    schedule: "Cohort timetable",
  },
  {
    accent: "violet",
    mark: "PR",
    shortcut: "PRODUCT",
    tagline: "Build useful digital products",
    title: "Product Management & Design",
    apiTitle: "Product Management and Design Bootcamp",
    description:
      "Research real problems, shape product strategy and design useful digital experiences.",
    duration: "12 weeks",
    format: "Live online",
    schedule: "Cohort timetable",
  },
  {
    accent: "green",
    mark: "CY",
    shortcut: "CYBER",
    tagline: "Protect modern systems",
    title: "Cybersecurity Bootcamp",
    apiTitle: "Cybersecurity Bootcamp",
    description:
      "Learn to identify threats, protect systems and support organisational security practices.",
    duration: "16 weeks",
    format: "Live online",
    schedule: "Cohort timetable",
  },
  {
    accent: "orange",
    mark: "BU",
    shortcut: "BUSINESS",
    tagline: "Lead business change",
    title: "Business Analysis & Project Management",
    apiTitle: "Business Analysis and Project Management Bootcamp",
    description:
      "Define business problems, manage requirements and lead projects with confidence.",
    duration: "3 months",
    format: "Live online",
    schedule: "Weekends",
  },
  {
    accent: "rose",
    mark: "MA",
    shortcut: "MARKETING",
    tagline: "Grow digital products",
    title: "Product & Digital Marketing",
    apiTitle: "Product and Digital Marketing Bootcamp",
    description:
      "Understand customers, create campaigns and grow digital products across key channels.",
    duration: "12 weeks",
    format: "Live online",
    schedule: "Cohort timetable",
  },
];

const CourseCard = ({
  course,
  cardRef,
  cohortLabel,
  payment,
  tuitionLoading,
}) => (
  <article
    ref={cardRef}
    className={`${styles.card} flex-none w-[calc(100vw-2rem)] sm:w-[420px] md:w-[560px] lg:w-[650px] min-h-[340px] bg-white border border-primary/10 rounded-3xl overflow-hidden grid home-grid-card shadow-course-card`}
  >
    <div
      className="text-white flex flex-col px-5 py-6 sm:px-6 sm:py-6"
      style={{ background: accents[course.accent] }}
    >
      <span className="text-secondary font-poppins tracking-[0.07em] text-xs font-extrabold leading-relaxed">
        PRACTICAL BOOTCAMP
      </span>
      <div
        className="w-20 h-20 sm:w-28 sm:h-28 font-poppins rounded-3xl border border-white/30 bg-white/[0.08] grid place-items-center mx-auto my-6 sm:my-9 text-2xl sm:text-4xl font-extrabold tracking-[-0.05em]"
        aria-hidden="true"
      >
        {course.mark}
      </div>
      <strong className="mt-auto text-sm font-poppins leading-snug">
        {course.tagline}
      </strong>
    </div>

    <div className="flex flex-col p-5 sm:p-6">
      <h3 className="text-primary font-poppins font-semibold tracking-[-0.025em] text-xl sm:text-2xl leading-tight mb-2.5">
        {course.title}
      </h3>
      <p className="text-gray text-sm font-poppins leading-[1.65] mb-4 min-h-[3.5rem]">
        {course.description}
      </p>

      <div className="grid grid-cols-3 border-y border-hire-line">
        {[
          ["Duration", course.duration],
          ["Format", course.format],
          ["Schedule", course.schedule],
        ].map(([label, value], index) => (
          <span
            key={label}
            className={`px-3 py-3 ${
              index !== 0 ? "border-l border-hire-line" : "pl-0"
            }`}
          >
            <small className="text-gray-2 text-xs font-poppins block mb-0.5">
              {label}
            </small>
            <strong className="text-default text-sm font-poppins">
              {value}
            </strong>
          </span>
        ))}
      </div>

      <div className="bg-hire-tint-blue text-gray font-poppins rounded-[11px] flex items-center justify-between gap-3 my-4 px-3.5 py-3 text-sm">
        <span>Next cohort</span>
        <strong className="text-primary font-poppins">{cohortLabel}</strong>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-auto">
        <p className="m-0">
          <small className="text-gray-2 text-xs block font-poppins">
            Tuition
          </small>
          <strong className="text-default text-xl block font-poppins">
            {tuitionLoading
              ? "Loading..."
              : payment?.outRightPaymentAmount
              ? `$${payment.outRightPaymentAmount}`
              : "Not specified"}
          </strong>
          {payment?.partPaymentAmount && (
            <span className="text-gray-2 text-xs font-poppins">
              Payment plan available
            </span>
          )}
        </p>
        <a
          href="https://study.utiva.co/"
          target="_blank"
          rel="noreferrer noopener"
          className="w-full sm:w-auto border-2 border-primary text-primary font-poppins rounded-[11px] min-h-[46px] inline-flex items-center justify-center gap-2 px-4 text-sm font-bold hover:bg-primary hover:text-white transition-colors"
        >
          View program{" "}
          <span aria-hidden="true">
            <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
          </span>
        </a>
      </div>
    </div>
  </article>
);

const ARROW_MAX_OFFSET = 68;
const ARROW_EDGE_BUFFER = 8;
const TRACK_MIN_PADDING = 2;

const HomeProgramsCarouselSection = () => {
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  // Not wrapped in Animate.Fade, so its ref is safe from Fade's child-cloning
  // (which silently drops refs placed on its direct child).
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [edgeSpace, setEdgeSpace] = useState(TRACK_MIN_PADDING);
  const [cohortData, setCohortData] = useState({});
  const [cohortLoading, setCohortLoading] = useState(false);

  const fetchCohortDateData = async () => {
    setCohortLoading(true);
    try {
      const params = { page: 1, limit: 1 };
      const response = await getCohortsApi(params);
      setCohortData(response?.data?.[0] || {});
      setCohortLoading(false);
    } catch (error) {
      setCohortLoading(false);
    }
  };

  // Tuition comes from the same courses API the course pages use, keyed by
  // each card's apiTitle, so the carousel never drifts from the real price.
  const [paymentByTitle, setPaymentByTitle] = useState({});
  const [tuitionLoading, setTuitionLoading] = useState(false);

  const fetchTuitionData = async () => {
    setTuitionLoading(true);
    try {
      const params = { page: 1, limit: 20 };
      const response = await getCoursesApi(params);
      const payments = {};
      (response?.data || []).forEach((item) => {
        payments[item?.title] = item?.paymentInEuro;
      });
      setPaymentByTitle(payments);
      setTuitionLoading(false);
    } catch (error) {
      setTuitionLoading(false);
    }
  };

  useEffect(() => {
    fetchCohortDateData();
    fetchTuitionData();
  }, []);

  const cohortLabel = cohortLoading
    ? "Loading..."
    : cohortData?.date || "Not specified";

  // offsetLeft is measured against the nearest *positioned* ancestor, which
  // here is the arrow buttons' wrapper rather than the track itself, so it
  // doesn't line up with track.scrollLeft. Derive a scroll-invariant
  // position from getBoundingClientRect instead.
  const getCardScrollTarget = (card) => {
    const track = trackRef.current;
    if (!track || !card) return 0;
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const paddingLeft = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    return cardRect.left - trackRect.left + track.scrollLeft - paddingLeft;
  };

  const scrollByAmount = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const rawTarget = Math.min(
      Math.max(track.scrollLeft + direction * track.clientWidth, 0),
      maxScroll
    );

    // Snap to the nearest card's actual left edge (rather than an assumed
    // uniform card width) so a screen-width jump never leaves a card
    // half-cut-off at the edge.
    let target = rawTarget;
    let closestDistance = Infinity;
    cardRefs.current.forEach((card) => {
      if (!card) return;
      const cardTarget = Math.min(getCardScrollTarget(card), maxScroll);
      const distance = Math.abs(cardTarget - rawTarget);
      if (distance < closestDistance) {
        closestDistance = distance;
        target = cardTarget;
      }
    });

    track.scrollTo({ left: target, behavior: "smooth" });
  };

  const scrollToCourse = (index) => {
    const track = trackRef.current;
    const card = cardRefs.current[index];
    if (!track || !card) return;
    setActiveIndex(index);
    track.scrollTo({ left: getCardScrollTarget(card), behavior: "smooth" });
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    let frame = null;
    const handleScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const scrollLeft = track.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Infinity;
        cardRefs.current.forEach((card, index) => {
          if (!card) return;
          const distance = Math.abs(getCardScrollTarget(card) - scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        setActiveIndex(closestIndex);
      });
    };

    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Measure how far the "container mx-auto" sits from the viewport edge, so
  // the full-bleed track's leading/trailing padding can match it exactly:
  // the first/last card lines up with the heading above at rest, and that
  // padding scrolls out of view (gets "covered") the moment scrolling starts,
  // letting every other card run edge-to-edge as intended by full-bleed.
  useEffect(() => {
    const updateEdgeSpace = () => {
      const container = containerRef.current;
      if (!container) return;
      setEdgeSpace(
        Math.max(TRACK_MIN_PADDING, container.getBoundingClientRect().left)
      );
    };

    updateEdgeSpace();
    window.addEventListener("resize", updateEdgeSpace);
    return () => window.removeEventListener("resize", updateEdgeSpace);
  }, []);

  // The arrow needs to sit inside that same margin without ever being
  // clipped by the viewport edge (margin shrinks to ~16px right at each
  // Tailwind container breakpoint) or overlapping the card it sits beside.
  const arrowOffset = -Math.max(
    ARROW_EDGE_BUFFER,
    Math.min(ARROW_MAX_OFFSET, edgeSpace - ARROW_EDGE_BUFFER)
  );

  return (
    <section
      className="py-16 md:py-24 px-4 bg-hire-tint-yellow"
      style={{
        backgroundImage:
          "linear-gradient(rgba(5,7,148,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(5,7,148,0.04) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
      }}
      id="programs"
    >
      <div className="container mx-auto" ref={containerRef}>
        <Animate.Fade bottom>
          <div className="grid home-grid-1115 items-end gap-6 lg:gap-20 mb-10 md:mb-12">
            <div>
              <p className="flex items-center font-poppins gap-3 text-primary text-sm font-extrabold tracking-[0.1em] uppercase mb-5">
                <span className="w-8 h-[3px] bg-secondary inline-block"></span>
                Program discovery
              </p>
              <h2 className="text-primary font-poppins font-bold tracking-[-0.045em] leading-[1.12] text-3xl md:text-4xl lg:text-6xl">
                What do you want to learn?
              </h2>
            </div>
            <p className="text-gray text-base font-poppins leading-[1.8]">
              Move through our career schools and compare the skills, format,
              next cohort and tuition for every featured program.
            </p>
          </div>
        </Animate.Fade>

        <Animate.Fade bottom>
          <div className="relative">
            <button
              type="button"
              onClick={() => scrollByAmount(-1)}
              aria-label="Scroll to previous program"
              style={{ left: arrowOffset }}
              className="hidden md:grid absolute z-20 font-poppins top-1/2 -translate-y-1/2 w-14 h-14 place-items-center rounded-full border-2 border-primary bg-white text-primary text-2xl hover:bg-primary hover:text-white transition-colors"
            >
              <span aria-hidden="true">
                <ArrowLeft fill="currentColor" />
              </span>
            </button>

            <div
              className="full-bleed py-1.5 pb-4"
              aria-label="Featured Utiva programs"
            >
              <div
                ref={trackRef}
                style={{ paddingLeft: edgeSpace, paddingRight: edgeSpace }}
                className={`${styles.track} flex gap-5 overflow-x-auto`}
              >
                {courses.map((course, index) => (
                  <CourseCard
                    key={course.title}
                    course={course}
                    cohortLabel={cohortLabel}
                    payment={paymentByTitle[course.apiTitle]}
                    tuitionLoading={tuitionLoading}
                    cardRef={(el) => (cardRefs.current[index] = el)}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => scrollByAmount(1)}
              aria-label="Scroll to next program"
              style={{ right: arrowOffset }}
              className="hidden md:grid absolute z-20 font-poppins top-1/2 -translate-y-1/2 w-14 h-14 place-items-center rounded-full border-2 border-primary bg-white text-primary text-2xl hover:bg-primary hover:text-white transition-colors"
            >
              <span aria-hidden="true">
                <ArrowRight fill="currentColor" />
              </span>
            </button>
          </div>
        </Animate.Fade>

        <Animate.Fade bottom>
          <div className="flex flex-nowrap sm:flex-wrap gap-3 mt-6 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0">
            {courses.map((course, index) => (
              <button
                key={course.title}
                type="button"
                onClick={() => scrollToCourse(index)}
                className={`flex-none inline-flex items-center gap-2 rounded-xl border font-poppins text-sm px-4 py-3 whitespace-nowrap transition-colors ${
                  activeIndex === index
                    ? "bg-primary border-primary"
                    : "bg-white border-hire-line hover:border-primary"
                }`}
              >
                <strong
                  className={
                    activeIndex === index ? "text-secondary" : "text-primary"
                  }
                >
                  {course.shortcut}
                </strong>
                <span
                  className={activeIndex === index ? "text-white" : "text-gray"}
                >
                  {course.title}
                </span>
              </button>
            ))}
          </div>
        </Animate.Fade>

        <div className="flex flex-wrap justify-between items-center gap-6 mt-6">
          <p className="text-gray text-sm font-poppins font-semibold m-0">
            Use the arrows, course shortcuts or swipe to find any programme
            immediately.
          </p>
          <a
            href="https://study.utiva.co/"
            target="_blank"
            rel="noreferrer noopener"
            className="bg-primary text-white rounded-xl font-poppins min-h-[50px] px-6 inline-flex items-center gap-3 text-sm font-bold hover:-translate-y-0.5 transition-all"
          >
            View All Programs{" "}
            <span aria-hidden="true">
              <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HomeProgramsCarouselSection;
